"use client";

import { useCallback, useEffect, useMemo } from "react";
import { FormField, FormItem, FormMessage } from "@/app/components/shadcn/form";
import { UseFormReturn, Path, FieldValues } from "react-hook-form";
import AgreementCheckbox from "./AgreementCheckbox";
import useAgreements from "@/app/lib/features/agreements/agreements.hook";
import AgreementsSkeleton from "@/app/components/elements/skeletons/AgreementsSkeleton";

interface AgreementsGroupProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  fieldNamePrefix?: string;
  disabled?: boolean;
  showTitle?: boolean;
  title?: string;
  className?: string;
  agreementIds?: number[];
  agreementErrors?: Record<number, string>;
  onAgreementChange?: (id: number) => void;
}

const AgreementsGroup = <T extends FieldValues>({
  form,
  fieldNamePrefix = "agreement",
  disabled = false,
  showTitle = true,
  title = "İzinler ve Onaylar",
  className = "space-y-4 pt-4 border-t border-gray-100",
  agreementIds,
  agreementErrors,
  onAgreementChange,
}: AgreementsGroupProps<T>) => {
  const { agreements, fetchAgreements, isIdle, isLoading } = useAgreements();

  useEffect(() => {
    if (isIdle) {
      fetchAgreements();
    }
  }, [isIdle, fetchAgreements]);

  const filteredAgreements = useMemo(() => {
    if (!agreementIds || agreementIds.length === 0) {
      return agreements;
    }
    return agreements.filter((agreement) =>
      agreementIds.includes(agreement.id),
    );
  }, [agreements, agreementIds]);

  const requiredAgreements = useMemo(
    () => filteredAgreements.filter((agreement) => agreement.isRequired),
    [filteredAgreements],
  );

  const optionalAgreements = useMemo(
    () => filteredAgreements.filter((agreement) => !agreement.isRequired),
    [filteredAgreements],
  );

  const getFieldName = useCallback(
    (agreementId: number) => {
      return `${fieldNamePrefix}_${agreementId}` as Path<T>;
    },
    [fieldNamePrefix],
  );

  // Idle veya Loading durumunda skeleton göster
  if (isIdle || isLoading) {
    return <AgreementsSkeleton className={className} count={3} />;
  }

  if (filteredAgreements.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {showTitle && (
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
      )}

      <div className="space-y-4">
        {/* Zorunlu Sözleşmeler */}
        {requiredAgreements.map((agreement) => (
          <FormField
            key={agreement.id}
            control={form.control}
            name={getFieldName(agreement.id)}
            render={({ field }) => (
              <FormItem>
                <AgreementCheckbox
                  agreement={agreement}
                  checked={Boolean(field.value)}
                  onChange={(checked) => {
                    field.onChange(checked);
                    if (checked) onAgreementChange?.(agreement.id);
                  }}
                  disabled={disabled}
                />
                {agreementErrors?.[agreement.id] && (
                  <p className="text-sm font-medium text-destructive">
                    {agreementErrors[agreement.id]}
                  </p>
                )}
              </FormItem>
            )}
          />
        ))}

        {/* Opsiyonel Sözleşmeler */}
        {optionalAgreements.length > 0 && requiredAgreements.length > 0 && (
          <div className="pt-2 border-t border-gray-100" />
        )}

        {optionalAgreements.map((agreement) => (
          <FormField
            key={agreement.id}
            control={form.control}
            name={getFieldName(agreement.id)}
            render={({ field }) => (
              <FormItem>
                <AgreementCheckbox
                  agreement={agreement}
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                  disabled={disabled}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
      </div>
    </div>
  );
};

export default AgreementsGroup;
